import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';

import { useApp } from 'contexts/AppContext';
import { useTheme } from 'contexts/ThemeContext';

import { getTabObjective } from 'utils/functions/index';
import Icon from 'utils/icons';
import { ACTIVATION, MEDIA } from 'utils/rest';

import { Text, Tooltip } from 'components/DataDisplay';
import { Modal } from 'components/Feedback';
import { FormSection } from 'components/FormSection';
import Image from 'components/Image';
import {
  Button,
  FunnelCalendar,
  Select,
  Switch,
  TextField
} from 'components/Inputs';
import { Container, Item, Row } from 'components/Layout';
import { ListField } from 'components/ListField';

import { enrichConversionLocations, mockedObjectives } from '../../utils';
import { DropdownSelect } from '../DropdownSelect';
import { ModalDelete } from '../ModalCampaign/ModalDelete';

const getPaid = campaign => {
  if (campaign.isCampaignGroup) {
    if (campaign.paid === '') {
      return 'none';
    }
    return campaign.paid;
  }
  if (campaign.paid) {
    return 'true';
  }
  return 'false';
};

const ModalUpdate = props => {
  const { themeColors } = useTheme();
  const { domainConfig = {} } = useApp();
  const { onClose, campaign, onDelete, config, onValidate } = props;
  const [allUsers, setAllUsers] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [openModalDelete, setOpenModalDelete] = useState('');
  const [usersSelected, setUsersSelected] = useState([]);
  const [status, setStatus] = useState(campaign.status);
  const [paid, setPaid] = useState(getPaid(campaign));
  const [tags, setTags] = useState([]);
  const [objective, setObjective] = useState(campaign.objective);
  const [isMetaLinked, setIsMetaLinked] = useState(false);
  const [objectivesByPlatform, setObjectivesByPlatform] = useState({});
  const [mediaOrderObjectives, setMediaOrderObjectives] = useState([]);
  const [
    geoTargetCampaignsSelection,
    setGeoTargetCampaignsSelection
  ] = useState(campaign.geoTargetCampaignsSelection || '');
  const [marginRate, setMarginRate] = useState(0);

  const [mediaLogosLoaded, setMediaLogosLoaded] = useState([]);
  const mediaLogos = mediaLogosLoaded;

  const [startDate, setStartDate] = useState(
    campaign.start !== null
      ? moment(campaign.start)
          .utc()
          .startOf('day')
          .format()
      : null
  );
  const [endDate, setEndDate] = useState(
    campaign.end !== null
      ? moment(campaign.end)
          .utc()
          .endOf('day')
          .format()
      : null
  );

  const platformKeys = useMemo(() => {
    const keysFromUsedPlatforms = Array.isArray(campaign?.usedPlatforms)
      ? campaign.usedPlatforms
      : [];

    const keysFromPlatforms = Array.isArray(campaign?.platforms)
      ? campaign.platforms.reduce((acc, item) => {
          if (item?.media) {
            acc.push(item.media);
          }
          return acc;
        }, [])
      : [];

    return [
      ...new Set([...keysFromUsedPlatforms, ...keysFromPlatforms])
    ].map(key => key.toLowerCase());
  }, [campaign]);

  const selectedGeoTargets = useMemo(() => {
    const activableMedias = Array.isArray(config?.activableMedias)
      ? config.activableMedias
      : [];

    return platformKeys.reduce((acc, platformKey) => {
      const activableMedia = activableMedias.find(item => {
        if (item?.key?.toLowerCase() === platformKey) {
          return true;
        }

        if (
          Array.isArray(item?.platforms) &&
          item.platforms.some(value => value?.toLowerCase() === platformKey)
        ) {
          return true;
        }

        return false;
      });

      if (activableMedia?.geoTargets?.length) {
        acc.push(activableMedia.geoTargets);
      }

      return acc;
    }, []);
  }, [config, platformKeys]);

  const hasGeoOption = useMemo(() => {
    return selectedGeoTargets.reduce((acc, geoTargets) => {
      if (acc) {
        return acc;
      }

      if (geoTargets.find(el => el.geo && !el.audience)) {
        return true;
      }

      return acc;
    }, false);
  }, [selectedGeoTargets]);

  const hasAudienceOption = useMemo(() => {
    return selectedGeoTargets.reduce((acc, geoTargets) => {
      if (acc) {
        return acc;
      }

      if (geoTargets.find(el => el.audience)) {
        return true;
      }

      return acc;
    }, false);
  }, [selectedGeoTargets]);

  const hasGeoPlusAudienceOption = useMemo(() => {
    return selectedGeoTargets.reduce((acc, geoTargets) => {
      if (acc) {
        return acc;
      }

      if (geoTargets.find(el => el.geo && el.audience)) {
        return true;
      }

      return acc;
    }, false);
  }, [selectedGeoTargets]);

  const geoTargetOptions = [];

  if (hasGeoOption) {
    geoTargetOptions.push({ label: 'Géographique', value: 'geo' });
  }

  if (hasAudienceOption) {
    geoTargetOptions.push({ label: 'Audience', value: 'audience' });
  }

  if (hasGeoPlusAudienceOption) {
    geoTargetOptions.push({
      label: 'Géo + audience',
      value: 'geoAndAudience'
    });
  }

  const canDisplayGeoTargetSelect = geoTargetOptions.length > 0;

  const isValidGeoTargetValue = value => {
    return geoTargetOptions.some(option => option.value === value);
  };

  const getLegacyGeoTargetsDefaultValue = () => {
    if (hasAudienceOption && hasGeoPlusAudienceOption) {
      return 'geo';
    }

    if (hasAudienceOption) {
      return 'audience';
    }

    if (hasGeoPlusAudienceOption) {
      return 'geoAndAudience';
    }

    return 'geo';
  };

  const getSingleGeoTargetsDefaultValue = geoTargets => {
    if (!geoTargets?.geo) {
      return null;
    }

    if (geoTargets.audience) {
      return geoTargets.isDefault ? 'geo' : 'audience';
    }

    return geoTargets.isDefault ? 'geo' : 'geoAndAudience';
  };

  const getDomainDefaultGeoTargetValue = () => {
    const domainDefaults = platformKeys.reduce((acc, platformKey) => {
      const foundDefaultTargeting = (
        domainConfig?.defaultTargetingTypesByMedia || []
      ).find(row => row?.media?.toLowerCase() === platformKey);

      if (isValidGeoTargetValue(foundDefaultTargeting?.targetingType)) {
        acc.push(foundDefaultTargeting.targetingType);
      }

      return acc;
    }, []);

    const uniqueDomainDefaults = [...new Set(domainDefaults)];

    if (uniqueDomainDefaults.length === 1) {
      return uniqueDomainDefaults[0];
    }

    return null;
  };

  const getGeoTargetsDefaultValue = () => {
    const geoTargetsDefaults = selectedGeoTargets.reduce((acc, geoTargets) => {
      const defaultValue = getSingleGeoTargetsDefaultValue(geoTargets);

      if (isValidGeoTargetValue(defaultValue)) {
        acc.push(defaultValue);
      }

      return acc;
    }, []);

    const uniqueGeoTargetsDefaults = [...new Set(geoTargetsDefaults)];

    if (uniqueGeoTargetsDefaults.length === 1) {
      return uniqueGeoTargetsDefaults[0];
    }

    return getLegacyGeoTargetsDefaultValue();
  };

  const getDefaultGeoTargetValue = () => {
    return getDomainDefaultGeoTargetValue() || getGeoTargetsDefaultValue();
  };

  const getGeoTarget = () => {
    if (geoTargetCampaignsSelection) {
      return geoTargetCampaignsSelection;
    }

    return getDefaultGeoTargetValue();
  };

  const getGeoTargetCampaignsScopes = () => {
    const value = getGeoTarget();

    if (value === 'geo') {
      return ['geo'];
    }

    if (value === 'audience') {
      return ['audience'];
    }

    if (value === 'geoAndAudience') {
      return ['geoAndAudience'];
    }

    return [];
  };

  const renderGeoTargetTooltip = () => (
    <Text
      component="span"
      style={{ whiteSpace: 'pre-line', color: themeColors.keep_light }}
    >
      {hasGeoOption && (
        <>
          <strong>Géographique :</strong> utilise des critères géo pour le setup
          auto.
        </>
      )}
      {hasAudienceOption && (
        <>
          {'\n'}
          <strong>Audience :</strong> utilise l’audience Ermes, uniquement sur
          les campagnes compatibles.
        </>
      )}
      {hasGeoPlusAudienceOption && (
        <>
          {'\n'}
          <strong>Géo + audience :</strong> combine l’audience Ermes et des
          critères géo, uniquement sur les campagnes compatibles.
        </>
      )}
    </Text>
  );

  const formatStartDate = date => {
    return date
      ? moment(date)
          .utc()
          .startOf('day')
          .format()
      : null;
  };

  const formatEndDate = date => {
    return date
      ? moment(date)
          .utc()
          .endOf('day')
          .format()
      : null;
  };

  const setPeriod = async ({ startDate: start, endDate: end }) => {
    setStartDate(formatStartDate(start));
    setEndDate(formatEndDate(end));
  };

  const validate = () => {
    const value = canDisplayGeoTargetSelect ? getGeoTarget() : undefined;

    onValidate({
      startDate,
      endDate,
      objective,
      objectivesByPlatform,
      paid,
      status,
      userIds: usersSelected,
      tags: tags.length !== 0 ? tags : [],
      isMetaLinked,
      geoTargetCampaigns: value === undefined ? undefined : value === 'geo',
      geoTargetCampaignsSelection: value,
      geoTargetCampaignsScopes:
        value === undefined ? [] : getGeoTargetCampaignsScopes(),
      marginRate: marginRate / 100
    });
  };

  useEffect(() => {
    const fetchLogos = async () => {
      try {
        const keys = mockedObjectives.platforms.reduce((acc, curr) => {
          acc.push(
            ...curr.platforms.map(p =>
              p === 'search' ? 'googleads' : p.toLowerCase()
            )
          );
          return acc;
        }, []);
        const logos = await MEDIA.getMediaLogosbyKeys(keys);
        setMediaLogosLoaded(logos);
      } catch (error) {
        console.error('Erreur lors du chargement des logos médias :', error);
      }
    };

    fetchLogos();
  }, []);

  useEffect(() => {
    const fetchObjectives = async () => {
      try {
        const objectives = await ACTIVATION.getMediaObjectives();
        setMediaOrderObjectives(objectives);
      } catch (error) {
        console.error(
          'Erreur lors du chargement des mediaOrderObjectives :',
          error
        );
      }
    };
    fetchObjectives();
  }, []);

  const findCampaignObjective = (mocked, campaignObjectiveKey) =>
    mocked.platforms.reduce((label, platform) => {
      if (label) return label;

      const obj = platform?.objectives?.find(
        o => o.objectiveKey === campaignObjectiveKey
      );

      if (obj) {
        return obj.subObjectives?.length
          ? `${obj.objectiveLabel} - ${obj.subObjectives[0].subObjectiveLabel}`
          : obj.objectiveLabel;
      }

      return null;
    }, null);

  const campaignObjectiveLabel = useMemo(
    () => findCampaignObjective(mockedObjectives, campaign.objective),
    [campaign.objective]
  );

  const enrichFilteredObjectives = (
    mediaObjectives,
    mocked,
    objectivesMap,
    objectiveLabel,
    usedPlatforms
  ) => {
    return usedPlatforms.map(media => {
      const platform = mocked.find(el => el.platforms.includes(media));
      const platformObjectives = mediaObjectives?.filter(obj =>
        platform?.platforms?.includes(obj?.platform)
      );

      const filteredObjectives = platform?.objectives
        ?.map(obj => {
          const matching = platformObjectives?.find(
            o => o.objectiveKey === obj.objectiveKey
          );
          if (matching) {
            const conversionLocations = enrichConversionLocations(
              matching,
              obj
            );

            return {
              ...obj,
              conversionLocations,
              subObjectives: obj.subObjectives.filter(sub =>
                matching.subObjectives.includes(sub.subObjectiveKey)
              )
            };
          }
          return null;
        })
        .filter(Boolean);

      const current = objectivesMap[media] || {};
      const hasSelectedObjective = !!current.objective;

      return {
        ...platform,
        platform: media,
        objectives: filteredObjectives,
        campaignObjectiveLabel:
          hasSelectedObjective && objectiveLabel
            ? objectiveLabel
            : 'Sélectionner un objectif',
        objective: current.objective || '',
        subObjective: current.subObjective || ''
      };
    });
  };

  const enrichedPlatforms = useMemo(
    () =>
      enrichFilteredObjectives(
        mediaOrderObjectives,
        campaign.mockedMedia,
        objectivesByPlatform,
        campaignObjectiveLabel,
        campaign.usedPlatforms
      ),
    [
      mediaOrderObjectives,
      campaign.mockedMedia,
      objectivesByPlatform,
      campaignObjectiveLabel,
      campaign.usedPlatforms
    ]
  );

  const mediaLogoMap = useMemo(() => {
    const map = {};
    if (Array.isArray(mediaLogos)) {
      mediaLogos.forEach(logo => {
        map[logo.key.toLowerCase()] = logo.base64Image;
      });
    }
    return map;
  }, [mediaLogos]);

  const updateObjectiveFromDropdown = (platformKey, campaignId, updaterFn) => {
    const simulatedPreviousOrders = [
      {
        id: campaignId,
        activableMedia: platformKey,
        objective: '',
        subObjective: ''
      }
    ];
    const updatedOrders = updaterFn(simulatedPreviousOrders);
    const updatedOrder = updatedOrders.find(
      order => order.activableMedia === platformKey
    );
    if (updatedOrder?.objective) {
      setObjectivesByPlatform(prev => ({
        ...prev,
        [platformKey]: {
          objective: updatedOrder.objective,
          subObjective: updatedOrder.subObjective || null
        }
      }));
    }
  };

  useEffect(() => {
    if (campaign) {
      setAllUsers(
        config.users.map(user => ({
          _id: user._id,
          name: user.username
        }))
      );
      setLoaded(true);
      setGeoTargetCampaignsSelection(
        campaign.geoTargetCampaignsSelection || ''
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaign]);

  useEffect(() => {
    if (campaign?.platforms?.length) {
      const initialObjectives = {};
      campaign.platforms.forEach(p => {
        initialObjectives[p.media] = p.objective || '';
      });
      setObjectivesByPlatform(initialObjectives);
    }
  }, [campaign]);

  const marginTooltipText =
    "La marge est une partie du budget média que nous n'enverrons pas à la plateforme média";

  const checkMarginValue = numValue => {
    // eslint-disable-next-line no-restricted-globals
    if (!isNaN(numValue) && numValue < 100 && numValue >= 0) {
      return true;
    }
    return false;
  };

  const handleChange = e => {
    const numValue = parseFloat(e);
    if (checkMarginValue(numValue)) {
      setMarginRate(numValue);
    }
  };

  return (
    <Modal
      {...props}
      onClose={() => {
        onClose();
      }}
      maxWidth="md"
    >
      <Container>
        <Row spacing={1}>
          <Item justify="flex-start">
            <FormSection title="Général" xs={3}>
              <Container>
                <Row spacing={1}>
                  <Item xs={6} justify="flex-end" style={{ alignItems: 'end' }}>
                    <Container>
                      <Row spacing={0} justify="flex-start">
                        <Item xs={11} justify="flex-start">
                          <Select
                            title="Objectif*"
                            options={getTabObjective.map(el => ({
                              value: el.key,
                              label: el.value
                            }))}
                            value={objective}
                            onChange={e => {
                              setObjective(e);
                            }}
                          />
                        </Item>
                      </Row>
                    </Container>
                  </Item>
                  <Item xs={6} justify="flex-end" style={{ alignItems: 'end' }}>
                    <Container>
                      <Row spacing={0} justify="flex-start">
                        <Item xs={11} justify="flex-start">
                          <FunnelCalendar
                            disableCampare
                            hidePeriodShortcuts
                            title="Dates"
                            required
                            start={startDate}
                            end={endDate}
                            onChange={setPeriod}
                            displayDate
                            onClearDate={() => {
                              setPeriod({ reset: true });
                            }}
                            displayClearDate={false}
                            inline
                            disabled
                          />
                        </Item>
                      </Row>
                    </Container>
                  </Item>
                </Row>
                <Row spacing={1}>
                  <Item xs={6} justify="flex-end" style={{ alignItems: 'end' }}>
                    <Container>
                      <Row spacing={0} justify="flex-start">
                        <Item xs={11} justify="flex-start">
                          <Select
                            title="Statut*"
                            options={[
                              { value: 'draft', label: 'Brouillon' },
                              { value: 'ready', label: 'Prête' },
                              { value: 'validated', label: 'Validée' },
                              { value: 'paused', label: 'Pause' },
                              { value: 'canceled', label: 'Annulée' },
                              { value: 'completed', label: 'Terminée' },
                              { value: 'active', label: 'Activée' }
                            ]}
                            value={status}
                            onChange={e => {
                              setStatus(e);
                            }}
                          />
                        </Item>
                      </Row>
                    </Container>
                  </Item>
                  <Item xs={6} justify="flex-end" style={{ alignItems: 'end' }}>
                    <Container>
                      <Row spacing={0} justify="flex-start">
                        <Item xs={11} justify="flex-start">
                          <Select
                            title="Paiement*"
                            options={[
                              { value: 'false', label: 'Non Payée' },
                              { value: 'true', label: 'Payée' }
                            ]}
                            value={paid}
                            disabled={status === 'draft'}
                            onChange={e => {
                              setPaid(e);
                            }}
                          />
                        </Item>
                      </Row>
                    </Container>
                  </Item>
                </Row>

                <Row spacing={1}>
                  <Item
                    xs={6}
                    justify="flex-start"
                    style={{ alignItems: 'start' }}
                  >
                    <Container>
                      <Row spacing={0} justify="flex-start">
                        <Item xs={11} justify="flex-start">
                          <Tooltip
                            style={{ width: '100%' }}
                            backGroundColor={themeColors.themeV2.black}
                            disableHoverListener={config.disabledDelete}
                            title="Seules les campagnes au statut différent de brouillon peuvent-être supprimées."
                          >
                            <Button
                              disabled={!config.disabledDelete}
                              variant="contained"
                              color={themeColors.delete}
                              widthSize="large"
                              style={{
                                height: '40px',
                                width: '100%',
                                marginBottom: '6px'
                              }}
                              onClick={() => {
                                setOpenModalDelete(true);
                              }}
                            >
                              <Text bold color="inherit">
                                Supprimer la campagne
                              </Text>
                            </Button>
                          </Tooltip>
                        </Item>
                      </Row>
                    </Container>
                  </Item>
                  <Item
                    xs={6}
                    justify="flex-end"
                    style={{ alignItems: 'end' }}
                  />
                </Row>
              </Container>
            </FormSection>
          </Item>
        </Row>

        <Row spacing={1}>
          <Item justify="flex-start">
            <FormSection title="Objectifs par plateforme" xs={6}>
              {enrichedPlatforms.map(platform => {
                const key =
                  platform.platform === 'search'
                    ? 'googleads'
                    : platform.platform.toLowerCase();
                const logo = mediaLogoMap[key];

                return (
                  <Container key={platform.platform}>
                    <Row spacing={1}>
                      <Item
                        justify="flex-start"
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          paddingBottom: '12px',
                          paddingTop: '6px'
                        }}
                      >
                        {logo && (
                          <Item xs={4} justify="flex-start">
                            <Image
                              src={logo}
                              alt={platform.platform}
                              height="38px"
                              style={{ marginRight: '12px' }}
                            />
                          </Item>
                        )}
                        <DropdownSelect
                          item={platform}
                          themeColors={themeColors}
                          mediaOrder={{
                            ...campaign,
                            activableMedia: platform.platform,
                            objective: platform.objective,
                            subObjective: platform.subObjective
                          }}
                          setMediaOrders={updaterFn =>
                            updateObjectiveFromDropdown(
                              platform.platform,
                              campaign.id,
                              updaterFn
                            )
                          }
                        />
                      </Item>
                    </Row>
                  </Container>
                );
              })}
            </FormSection>
          </Item>
        </Row>

        <Row spacing={1}>
          <Item justify="flex-start">
            <FormSection title="Tags" xs={6}>
              <Container>
                <Row justify="flex-start" spacing={1}>
                  <Text justify="flex-start" fontWeight={400} fontSize="14px">
                    Sélectionnez des tags pour votre campagne, ou écrivez-en un
                    nouveau puis appuyez sur <strong>Entrée</strong>. Pour une
                    multicampagne, les tags seront copiés à l’ensemble de ses
                    campagnes enfants.
                  </Text>
                </Row>
              </Container>
              <Container style={{ marginTop: '12px' }}>
                <Row spacing={0}>
                  <Item xs={12} justify="flex-start">
                    <Container>
                      <Row spacing={0} justify="flex-start">
                        <Item xs={11} justify="flex-start">
                          {loaded && (
                            <ListField
                              options={config.tags.map(el => ({ tag: el }))}
                              values={tags.map(el => ({ tag: el }))}
                              onChange={e => {
                                setTags(e.map(({ tag }) => tag));
                              }}
                              searchOptionsKeys={['tag']}
                              actionTxt="Ajouter un tag"
                              deleteAction
                              keyEnter
                            />
                          )}
                        </Item>
                      </Row>
                    </Container>
                  </Item>
                  <Item xs={6} />
                </Row>
              </Container>
            </FormSection>
          </Item>
        </Row>
        <Row spacing={1}>
          <Item justify="flex-start">
            <FormSection title="Accès à la campagne" xs={3}>
              <Container>
                <Row spacing={0}>
                  <Item xs={12} justify="flex-start">
                    <Container>
                      <Row spacing={0} justify="flex-start">
                        <Item xs={11} justify="flex-start">
                          {loaded && (
                            <ListField
                              options={allUsers.map(item => ({
                                _id: item._id,
                                name: item.name
                              }))}
                              searchOptionsKeys={['name', '_id']}
                              values={usersSelected}
                              onChange={e => {
                                setUsersSelected(e);
                              }}
                              actionTxt="Ajouter"
                              deleteAction
                            />
                          )}
                        </Item>
                      </Row>
                    </Container>
                  </Item>
                  <Item xs={6} />
                </Row>
              </Container>
            </FormSection>
            {openModalDelete && (
              <ModalDelete
                open
                onClose={() => {
                  setOpenModalDelete(false);
                }}
                onDelete={() => {
                  setOpenModalDelete(false);
                  onDelete();
                }}
              />
            )}
          </Item>
        </Row>

        <Row spacing={1}>
          <Item justify="flex-start">
            <FormSection title="Setup auto" xs={3}>
              <Container>
                <Row spacing={1}>
                  {canDisplayGeoTargetSelect && (
                    <Item xs={12} justify="flex-start">
                      <Container>
                        <Row spacing={0} justify="flex-start">
                          <Item flex>
                            <Select
                              value={getGeoTarget()}
                              title="Ciblage"
                              options={geoTargetOptions}
                              tooltip={renderGeoTargetTooltip()}
                              onChange={value => {
                                setGeoTargetCampaignsSelection(value);
                              }}
                              small
                            />
                          </Item>
                        </Row>
                      </Container>
                    </Item>
                  )}
                  <Item xs={12} justify="flex-start">
                    <Container>
                      <Row spacing={0} justify="flex-start">
                        <Item flex>
                          <Switch
                            title="Dépense automatique Meta&nbsp;&nbsp;"
                            checked={isMetaLinked}
                            options={['Non', 'Oui']}
                            color={themeColors.black}
                            onChange={setIsMetaLinked}
                          />
                        </Item>
                      </Row>
                    </Container>
                  </Item>
                  <Container>
                    <Row spacing={0} justify="flex-start">
                      <Item justify="flex-start" flex xs={3}>
                        <TextField
                          unit="%"
                          title="Taux de marge"
                          value={marginRate}
                          tooltip={
                            <Text
                              component="span"
                              style={{ whiteSpace: 'pre-line' }}
                            >
                              {marginTooltipText}
                            </Text>
                          }
                          onChange={handleChange}
                          type="number"
                          inputProps={{ min: 0, max: 99, step: 1 }}
                          decimalPlaces={1}
                        />
                      </Item>
                    </Row>
                  </Container>
                  <Item xs={6} />
                </Row>
              </Container>
            </FormSection>
          </Item>
        </Row>
        <Row
          spacing={1}
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            marginTop: '50px'
          }}
        >
          <Button
            variant="contained"
            size="medium"
            color={themeColors.black}
            onClick={() => {
              validate();
            }}
          >
            <Icon icon="right" />
            <Text fontSize="15px" color="white" style={{ marginLeft: '5px' }}>
              Valider
            </Text>
          </Button>
        </Row>
      </Container>
    </Modal>
  );
};

ModalUpdate.defaultProps = {
  onClose: () => null,
  onValidate: () => {},
  onDelete: () => {}
};

ModalUpdate.propTypes = {
  onClose: PropTypes.func,
  config: PropTypes.shape().isRequired,
  campaign: PropTypes.shape().isRequired,
  onValidate: PropTypes.func,
  onDelete: PropTypes.func
};

export default ModalUpdate;
